import { FC, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  SAMPLE_TEMPLATES,
  RATIOS,
  type TemplateFilter,
  type TemplateScope,
} from "./data";

const SearchIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

const PlusIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const templatePreviewClass = (layout: string) => {
  if (layout === "split") return "is-split";
  if (layout === "full" || layout === "slide") return "is-full";
  return "";
};

const TemplateThumb: FC<{ layout: string; humanColor: string }> = ({
  layout,
  humanColor,
}) => {
  const variant = templatePreviewClass(layout);
  const isFull = variant === "is-full";
  return (
    <div className={`adh-template-visual${variant ? ` ${variant}` : ""}`}>
      <span className="adh-tp-slide" />
      {isFull ? null : <i className="adh-tp-human" style={{ background: humanColor }} />}
    </div>
  );
};

export const AiDigitalHumanTemplates: FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<TemplateFilter>("all");
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(false);
  const [toast, setToast] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [ratio, setRatio] = useState("16:9");
  const [style, setStyle] = useState("商务 · 沉稳");
  const [avatarOn, setAvatarOn] = useState(true);
  const [subtitleOn, setSubtitleOn] = useState(false);
  const [avatarSize, setAvatarSize] = useState(26);
  const [slideSize, setSlideSize] = useState(70);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2600);
  };

  const filtered = useMemo(() => {
    const base =
      filter === "all"
        ? SAMPLE_TEMPLATES
        : SAMPLE_TEMPLATES.filter((t) => t.scope === filter);
    return base.filter((t) => t.name.includes(search));
  }, [filter, search]);

  const scopeLabel = (scope: TemplateScope) =>
    scope === "public" ? "公共模板" : "我的模板";

  return (
    <div className="adh-page">
      <header className="adh-page-header">
        <div>
          <span className="adh-eyebrow">TEMPLATES</span>
          <h1>我的模板</h1>
          <p>模板保存全局布局并绑定形象，不绑定声音。</p>
        </div>
        <div className="adh-page-actions">
          <button
            className="adh-secondary-btn"
            type="button"
            onClick={() => navigate("/hub/ai-digital-human")}
          >
            返回创作
          </button>
          <button
            className="adh-primary-btn"
            type="button"
            onClick={() => setModal(true)}
          >
            <PlusIcon /> 新建模板
          </button>
        </div>
      </header>

      <div className="adh-toolbar">
        <div className="adh-filter-tabs">
          <button
            className={filter === "all" ? "active" : ""}
            type="button"
            onClick={() => setFilter("all")}
          >
            全部
          </button>
          <button
            className={filter === "public" ? "active" : ""}
            type="button"
            onClick={() => setFilter("public")}
          >
            公共模板
          </button>
          <button
            className={filter === "mine" ? "active" : ""}
            type="button"
            onClick={() => setFilter("mine")}
          >
            我的模板
          </button>
        </div>
        <label className="adh-search">
          <SearchIcon />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜索模板"
          />
        </label>
      </div>

      <div className="adh-template-grid">
        {filtered.map((t) => (
          <article
            key={t.id}
            className="adh-template-card"
            data-template-scope={t.scope}
          >
            <TemplateThumb layout={t.layout} humanColor={t.humanColor} />
            <div className="adh-template-info">
              <span>
                <strong>{t.name}</strong>
                <small>
                  {scopeLabel(t.scope)} · {t.ratio}
                </small>
              </span>
              {t.scope === "mine" ? (
                <div className="adh-card-menu">
                  <button type="button" title="重命名" aria-label={`重命名${t.name}`}>
                    ✎
                  </button>
                  <button type="button" title="删除" aria-label={`删除${t.name}`}>
                    ×
                  </button>
                </div>
              ) : null}
            </div>
          </article>
        ))}
      </div>

      {modal && (
        <div className="adh-modal-backdrop" onClick={() => setModal(false)}>
          <div
            className="adh-modal adh-modal--xl"
            onClick={(e) => e.stopPropagation()}
          >
            <header>
              <h3>新建模板</h3>
              <p>填写模板名称，选择比例、数字人、背景和字幕后保存。</p>
              <button
                type="button"
                className="adh-modal-close"
                onClick={() => setModal(false)}
              >
                ×
              </button>
            </header>
            <div className="adh-modal-body adh-modal-body--split">
              <div className="adh-template-form">
                <label className="adh-form-field">
                  <span>模板名称</span>
                  <input
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="请输入模板名称"
                  />
                </label>
                <div className="adh-form-row">
                  <label className="adh-form-field">
                    <div className="adh-select-wrap">
                      <label>视频比例</label>
                      <select
                        value={ratio}
                        onChange={(e) => setRatio(e.target.value)}
                      >
                        {RATIOS.map((r) => (
                          <option key={r}>{r}</option>
                        ))}
                      </select>
                      <ChevronDownIcon />
                    </div>
                  </label>
                  <label className="adh-form-field">
                    <span>风格</span>
                    <div className="adh-select-wrap">
                      <select
                        value={style}
                        onChange={(e) => setStyle(e.target.value)}
                      >
                        <option>商务 · 沉稳</option>
                        <option>活泼 · 明亮</option>
                        <option>科技 · 冷峻</option>
                      </select>
                      <ChevronDownIcon />
                    </div>
                  </label>
                </div>
                <div className="adh-form-row adh-form-row--between">
                  <span>开启数字人</span>
                  <button
                    className={`adh-switch ${avatarOn ? "on" : ""}`}
                    type="button"
                    onClick={() => setAvatarOn((v) => !v)}
                  >
                    <i />
                  </button>
                </div>
                {avatarOn && (
                  <div className="adh-form-row">
                    <label className="adh-form-field">
                      <span>形象</span>
                      <div className="adh-select-wrap">
                        <select>
                          <option>思萌</option>
                          <option>林溪</option>
                        </select>
                        <ChevronDownIcon />
                      </div>
                    </label>
                    <label className="adh-form-field">
                      <span>布局</span>
                      <div className="adh-select-wrap">
                        <select>
                          <option>课件＋角落数字人</option>
                          <option>左右分屏</option>
                        </select>
                        <ChevronDownIcon />
                      </div>
                    </label>
                  </div>
                )}
                <div className="adh-form-row adh-form-row--between">
                  <span>字幕</span>
                  <button
                    className={`adh-switch ${subtitleOn ? "on" : ""}`}
                    type="button"
                    onClick={() => setSubtitleOn((v) => !v)}
                  >
                    <i />
                  </button>
                </div>
                <div className="adh-slider-row">
                  <label>
                    课件大小 <b>{slideSize}%</b>
                  </label>
                  <input
                    type="range"
                    min={8}
                    max={100}
                    value={slideSize}
                    onChange={(e) => setSlideSize(Number(e.target.value))}
                  />
                </div>
                {avatarOn && (
                  <div className="adh-slider-row">
                    <label>
                      数字人大小 <b>{avatarSize}%</b>
                    </label>
                    <input
                      type="range"
                      min={8}
                      max={100}
                      value={avatarSize}
                      onChange={(e) => setAvatarSize(Number(e.target.value))}
                    />
                  </div>
                )}
              </div>
              <div className="adh-template-preview">
                <div className="adh-preview-label">预览</div>
                <div
                  className="adh-preview-canvas"
                  style={{ aspectRatio: ratio.replace(":", "/") }}
                >
                  <div className="adh-preview-slide">
                    <span className="adh-preview-tag">CHAPTER 01</span>
                    <strong>什么是 AI 产品经理？</strong>
                    <p>理解角色边界、核心能力与企业应用场景。</p>
                    <div className="adh-preview-bars">
                      <i />
                      <i />
                      <i />
                      <i />
                    </div>
                  </div>
                  {avatarOn && (
                    <div
                      className="adh-preview-human"
                      style={{ width: `${avatarSize}%`, background: "#b85c5c" }}
                    >
                      <span />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <footer>
              <button
                className="adh-secondary-btn"
                type="button"
                onClick={() => setModal(false)}
              >
                取消
              </button>
              <button
                className="adh-primary-btn"
                type="button"
                onClick={() => {
                  setModal(false);
                  showToast("已保存模板");
                }}
              >
                保存模板
              </button>
            </footer>
          </div>
        </div>
      )}

      {toast && (
        <div className="adh-toast" role="status">
          {toast}
        </div>
      )}
    </div>
  );
};
